import PlayCircle from '@mui/icons-material/PlayCircle'
import {
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import React, { Suspense, useContext, useEffect, useMemo, useState } from 'react'
import { TrackResponse } from 'src/common/tracks/TrackClass'
import { CStrong } from 'src/components/coreUIMimicComponent'
import { APArticleTextContainer } from 'src/consumer/components/ArticlePage/ArticlePageStyles'
import { QuoteAndReflectionStandaloneComponent } from 'src/consumer/components/DailyPlay/QuoteAndReflection'
import { MentalStateTypeComponentDialog } from 'src/consumer/components/MentalStateComponent'
import {
  CustomInput,
  CustomSelect,
  CustomTextArea,
  selectTypes,
} from 'src/consumer/components/helperComponents'
import {
  CustomAsyncCreatablePaginate,
  CustomAsyncPaginate,
} from 'src/consumer/components/helperComponents/dynamic'
import {
  checkAndGetTargetValue,
  getParseDataFromHtmlString,
  sortFn,
} from 'src/consumer/helpers/utilFunctions'
import { getFeatureItemBySlug, getQuoteData } from 'src/consumer/services/featureServices'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import {
  categoryToDailyPlayType,
  checkIfStringIsValid,
  getActivityObject,
  getOptions,
  returnLoadOptions,
} from '../utils'
import { activityPlayerUrl, handleActivityPlanClick } from './activity-utils'
import {
  DescriptionSelectionDialogStyles,
  DetailsInTextDialogStyles,
} from './activityComponentStyledComponents'
import ActivityFeatureCreationComponentInjector from './activityFeatureCreationComponents/ActivityFeatureCreationComponentInjector'
import ActivityDescriptionSelection from './activitySectionComponents/ActivityDescriptionSelection'
import {
  ActivitySectionContainer,
  CreationDialogStyledComponent,
  StyledActivitySelectorContainer,
} from './styledComponents'
import RichTextEditorQuill from 'src/consumer/components/Helpers/RichTextEditorQuill'
import TrackUploadComponent from 'src/consumer/components/Helpers/TrackUploadComponent'

function ActivityComponent({
  initialObject = {},
  imageObject: activityObject,
  setImageObject: setActivityObject,
  handleChangeObject,
  customOptions = {},
  disabled = false,
  view = 'default',
  day,
  children,
}) {
  const [loading, setLoading] = useState({ activityId: undefined })
  const [courseOptions, setCourseOptions] = useState()
  const [quote, setQuote] = useState(false)
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false)
  const [showMentalStateDialog, setShowMentalStateDialog] = useState(false)
  const [mentalStateTypeData, setMentalStateTypeData] = useState()
  const [activityPlayerData, setActivityPlayerData] = useState()
  const ActivitySelectComponent = useMemo(() => {
    return activityObject?.metaCategoryId === 'rest'
      ? CustomAsyncCreatablePaginate
      : CustomAsyncPaginate
    // return CustomAsyncPaginate
  }, [activityObject?.metaCategoryId])
  const [toCreateItem, setToCreateItem] = useState()
  const { mentorProfileData } = useContext(MentorProfileContext)
  const [restType, setRestType] = useState(restTypes.my)
  const [showDescriptionModal, setShowDescriptionModal] = useState(false)
  const [activityOptionsFilters, setActivityOptionsFilters] = useState({
    languages: [],
  })

  const dropDownOptions = {
    ...customOptions,
  }
  const allCategories = dropDownOptions.metaCategories?.sort((cat1, cat2) =>
    sortFn(cat1?.title, cat2.title),
  )

  const selectedCategory = useMemo(() => {
    return allCategories.find((cat) => cat.id === activityObject?.metaCategoryId)
  }, [activityObject?.metaCategoryId])

  async function loadActivityItemOptions(inputValue, callback, { page, size }) {
    // tempCard.type === 'course' ? cardLanguages : languages,
    setLoading((prev) => ({ ...prev, activityId: true }))
    const dpType = categoryToDailyPlayType(activityObject?.metaCategoryId, dropDownOptions)

    const foundEnglish = 'en'
    const filterLanguageIds = activityOptionsFilters?.languages?.map((lang) => lang.id)
    let dropDownOptionsData
    dropDownOptionsData = await getOptions(
      dpType,
      activityObject?.metaCategoryId,
      filterLanguageIds || [foundEnglish],
      size,
      page,
      inputValue,
      false,
      mentorProfileData.id,
      restType,
    )
    setLoading((prev) => ({ ...prev, activityId: undefined }))
    return returnLoadOptions(dropDownOptionsData)
  }

  const descriptionSentence = `We are offering you ${activityObject?.customCategoryTitle?.toLowerCase()} ${
    day !== undefined && `on day ${day}`
  } so as to ${activityObject?.descriptionZ?.toLowerCase()} because ${activityObject?.descriptionW?.toLowerCase()}`

  function changeActivityType(event) {
    handleChangeObject(event)
    const foundCategory = allCategories.find((cat) => cat.id === event.target.value)
    handleChangeObject({
      target: {
        value: foundCategory?.singleTitle || foundCategory?.title,
        id: 'customCategoryTitle',
      },
    })
    handleChangeObject({
      target: {
        value: null,
        id: 'activityId',
      },
    })
  }

  async function changeActivityId(selectedValue) {
    setLoading((prev) => ({ ...prev, activityId: true }))
    if (selectedValue?.__isNew__) {
      setToCreateItem(selectedValue)
      setLoading((prev) => ({ ...prev, activityId: false }))
      return
    }
    let selectedValueToUse = selectedValue
    const selectedCategory = allCategories.find((cat) => cat.id === activityObject?.metaCategoryId)
    let currentCategoryTitle = activityObject?.customCategoryTitle
    const customFields =
      selectedCategory?.categoryType === 'course' ? { courseId: selectedValue?.course?.id } : {}
    if (selectedCategory?.categoryType === 'quotes') {
      const foundQuote = await getQuoteData(selectedValue?.id, false)
      const foundReflection = await getFeatureItemBySlug(
        'journal',
        foundQuote.data.journalId,
        false,
      )
      currentCategoryTitle = foundReflection?.data?.data?.journal?.title
      selectedValueToUse = {
        ...foundQuote.data,
        ...selectedValue,
      }
    }

    setLoading((prev) => ({ ...prev, activityId: false }))
    setToCreateItem()
    setActivityObject((prev) => ({
      ...prev,
      activityId: selectedValueToUse,
      customFields: customFields,
      customCategoryTitle: currentCategoryTitle,
    }))
  }

  function handleChangeTrack(eventPrev) {
    if (typeof eventPrev === 'object') {
      setActivityObject((prev) => {
        return {
          ...prev,
          track: {
            ...(prev?.track || TrackResponse),
            [eventPrev.target.id]: checkAndGetTargetValue(eventPrev),
          },
        }
      })
    } else {
      setActivityObject((prev) => ({
        ...prev,
        track: eventPrev(prev?.track || {}),
      }))
    }
  }

  function closeCreationModal() {
    setToCreateItem()
  }

  async function Setup() {
    if (!!activityObject?.activityId) {
      setLoading((prev) => ({ ...prev, activityId: true }))
      const isIdTitleAlreadyThere = typeof activityObject?.activityId === 'object'
      let foundActivityId = activityObject?.activityId
      if (!isIdTitleAlreadyThere) {
        foundActivityId = await getActivityObject(
          activityObject,
          activityObject?.metaCategoryId,
          dropDownOptions,
          mentorProfileData.id,
        )
      }
      setActivityObject((prev) => ({
        ...prev,
        activityId: foundActivityId,
      }))
      setLoading((prev) => ({ ...prev, activityId: false }))
    }
  }

  useEffect(() => {
    console.log({ lang: customOptions.languages })
  }, [customOptions.languages])

  useEffect(() => {
    Setup()
  }, [activityObject.id])

  useEffect(() => {
    setCourseOptions(undefined)
  }, [activityObject?.metaCategoryId])

  useEffect(() => {
    setActivityObject((prev) => ({
      ...prev,
      descriptionSentence: descriptionSentence,
    }))
  }, [activityObject.customCategoryTitle, activityObject.descriptionZ, activityObject.descriptionW])

  return disabled ? (
    <ActivityComponentReadOnly imageObject={activityObject} allCategories={allCategories} />
  ) : (
    <div className="d-flex flex-column gap-4 mt-4">
      <SectionComponent heading={'Card Details'} first>
        <div className="col-md-6">
          <CustomInput
            id="activityTitle"
            label="Title (as visible to user)"
            value={activityObject.activityTitle}
            onChange={handleChangeObject}
            disabled={disabled}
            placeholder="Enter a activity title...."
            invalid={!checkIfStringIsValid(activityObject.activityTitle)}
            required
          />
        </div>
        <div className="col-md-6">
          <CustomInput
            id="customCategoryTitle"
            label="Activity Type"
            value={activityObject.customCategoryTitle}
            onChange={handleChangeObject}
            disabled={disabled}
            invalid={!checkIfStringIsValid(activityObject.customCategoryTitle)}
            placeholder="Enter a title...."
            required
          />
        </div>
      </SectionComponent>
      <SectionComponent heading={'On Tap Details'}>
        <ButtonBase
          sx={{
            padding: '10px',
            alignItems: 'flex-start',
            justifyContent: 'center',
            textAlign: 'left',
          }}
          onClick={() => {
            setShowDescriptionModal(true)
          }}
        >
          <span style={{ width: '100%' }}>
            <strong>Description Sentence: </strong>
            {activityObject.customDescription ? (
              <span>{activityObject.customDescription}</span>
            ) : (
              <span>
                We are offering you{' '}
                <strong style={underLineStyled}>
                  {activityObject.customCategoryTitle?.toLowerCase() ||
                    'Custom Activity Type Title'}
                </strong>{' '}
                {day !== undefined && (
                  <>
                    on <strong>day {day} </strong>
                  </>
                )}
                so as to{' '}
                <strong style={underLineStyled}>
                  {activityObject.descriptionZ?.toLowerCase() ||
                    'What will this activity do to you'}
                </strong>{' '}
                because{' '}
                <strong style={underLineStyled}>
                  {activityObject.descriptionW?.toLowerCase() || 'Why am I offering you this today'}
                </strong>
              </span>
            )}
          </span>
        </ButtonBase>
        {/* <div className="py-2">
          <strong>Description Sentence: </strong>
          <span>
            We are offering you{' '}
            <strong style={underLineStyled}>
              {activityObject.customCategoryTitle?.toLowerCase() || 'Custom Activity Type Title'}
            </strong>{' '}
            {day !== undefined && (
              <>
                on <strong>day {day} </strong>
              </>
            )}
            so as to{' '}
            <strong style={underLineStyled}>
              {activityObject.descriptionZ?.toLowerCase() || 'What will this activity do to you'}
            </strong>{' '}
            because{' '}
            <strong style={underLineStyled}>
              {activityObject.descriptionW?.toLowerCase() || 'Why am I offering you this today'}
            </strong>
          </span>
        </div>
        <div className="col-md-6">
          <CustomTextArea
            id="descriptionZ"
            label="What will this activity do to you"
            value={activityObject.descriptionZ}
            onChange={handleChangeObject}
            placeholder="Enter details...."
            disabled={disabled}
            invalid={!checkIfStringIsValid(activityObject.descriptionZ)}
            minHeight="30px"
            required
          />
        </div>
        <div className="col-md-6">
          <CustomTextArea
            id="descriptionW"
            label="Why am I offering you this today"
            value={activityObject.descriptionW}
            onChange={handleChangeObject}
            placeholder="Enter details...."
            disabled={disabled}
            invalid={!checkIfStringIsValid(activityObject.descriptionW)}
            minHeight="30px"
            required
          />
        </div> */}
      </SectionComponent>
      <SectionComponent heading="Activity Details">
        <div style={{ zIndex: 0 }} className="col-md-12 mb-4">
          <h5 className="mb-3">Details in media (prioritized over text)</h5>
          <TrackUploadComponent
            trackObject={activityObject.track}
            handleChange={handleChangeTrack}
            disabled={disabled}
          />
        </div>
        <div className="col-md-12 mb-5" style={{ height: '600px' }}>
          <h5 className="mb-3">Details in text</h5>
          <RichTextEditorQuill
            id={'content'}
            htmlContent={activityObject.content}
            handleChangeHtml={handleChangeObject}
            disabled={disabled}
          />
        </div>
      </SectionComponent>
      <SectionComponent heading="Actual Aid Used">
        {selectedCategory?.categoryType === 'rest' && (
          <div className="col-md-12 d-flex justify-content-center align-items-center">
            <ToggleButtonGroup
              value={restType}
              exclusive
              size="small"
              onChange={(e, val) => setRestType(val)}
              aria-label="Rest Type"
              sx={{ width: '235px' }}
            >
              <ToggleButton sx={{ width: '50%' }} value={restTypes.my}>
                My
              </ToggleButton>
              <ToggleButton sx={{ width: '50%' }} value={restTypes.openToAll}>
                Open To all
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        )}
        <div className="col-md-12">
          <CustomSelect
            id="metaCategoryId"
            key={activityObject?.id}
            value={activityObject?.metaCategoryId}
            options={allCategories?.sort((a, b) => a.title.localeCompare(b.title))}
            getOptionValue={(option) => option?.id}
            getOptionLabel={(option) => option?.name}
            label="Category"
            selectType={selectTypes.string}
            onChangeEvent={changeActivityType}
            isDisabled={disabled}
            required
            invalid={!checkIfStringIsValid(activityObject?.metaCategoryId)}
          />
        </div>
        {selectedCategory?.categoryType === 'course' && (
          <div className="col-md-12">
            <CustomSelect
              id="langs"
              menuPortalTarget={document.body}
              isMulti
              label="Content Languages"
              value={activityOptionsFilters.languages}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => `${option.language} - ${option.region}`}
              options={customOptions.languages?.sort((a, b) =>
                `${a.language} - ${a.region}`.localeCompare(`${b.language} - ${b.region}`),
              )}
              onChangeEvent={(e) => {
                setActivityOptionsFilters((prev) => ({ ...prev, languages: e.target.value }))
                setCourseOptions()
              }}
              placeholder={`Select languages...`}
              selectType={selectTypes.object}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 99999 }) }}
            />
          </div>
        )}
        <div className="col-md-12 d-flex justify-content-stretch align-items-end gap-1">
          <StyledActivitySelectorContainer>
            <Suspense fallback={<div style={{ padding: '10px' }}>Loading...</div>}>
              <ActivitySelectComponent
                cacheUniqs={[
                  activityObject?.metaCategoryId,
                  restType,
                  activityOptionsFilters.languages,
                ]}
                id="activityItem"
                label="Aid"
                value={activityObject?.activityId}
                loadOptions={loadActivityItemOptions}
                isLoading={loading?.activityId}
                debounceTimeout={500}
                createOptionPosition="first"
                additional={{ page: 1, size: 100 }}
                isDisabled={disabled}
                onChange={changeActivityId}
                placeholder={`Select a ${
                  selectedCategory?.singleTitle || selectedCategory?.title || 'Aid'
                }`}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 99999 }) }}
              />
            </Suspense>
          </StyledActivitySelectorContainer>
          {!loading?.activityId &&
            !!activityObject?.activityId &&
            !!activityPlayerUrl(
              categoryToDailyPlayType(activityObject?.metaCategoryId, dropDownOptions),
              activityObject?.activityId,
              { setQuote, setQuoteDialogOpen, setShowMentalStateDialog },
            ) && (
              <IconButton
                sx={{ marginBottom: '3px' }}
                onClick={() => {
                  handleActivityPlanClick(
                    categoryToDailyPlayType(activityObject?.metaCategoryId, dropDownOptions),
                    activityObject?.activityId,
                    {
                      setQuote,
                      setQuoteDialogOpen,
                      setShowMentalStateDialog,
                      setMentalStateTypeData,
                    },
                  )
                }}
              >
                <PlayCircle />
              </IconButton>
            )}
        </div>
      </SectionComponent>
      <SectionComponent heading="More...">
        <div className="col-md-6">
          <CustomTextArea
            id="whatItDoes"
            colNumber={6}
            label="What it does"
            value={activityObject.whatItDoes}
            disabled={disabled}
            onChange={handleChangeObject}
            placeholder="Enter details...."
            minHeight="30px"
          />
        </div>
        <div className="col-md-6">
          <CustomTextArea
            id="whyOffered"
            colNumber={6}
            label="Why offered"
            value={activityObject.whyOffered}
            disabled={disabled}
            onChange={handleChangeObject}
            placeholder="Enter details...."
            minHeight="30px"
          />
        </div>
        <CustomTextArea
          id="internalNote"
          label="Internal Notes"
          value={activityObject.internalNote}
          disabled={disabled}
          onChange={handleChangeObject}
          placeholder="Enter your notes...."
        />
        {children}
      </SectionComponent>

      <QuoteAndReflectionStandaloneComponent
        isQuoteModalOpen={quoteDialogOpen}
        setIsQuoteModalOpen={setQuoteDialogOpen}
        data={quote}
        callSource={quote?.src}
        callSourceId={quote?.srcId}
        afterSubmissionFunction={() => {}}
        journalData={{}}
        setJournalData={() => {}}
        userFavoriteData={() => {}}
      />
      <MentalStateTypeComponentDialog
        mentalStateDialogData={mentalStateTypeData}
        afterSubmitFunction={() => {}}
        open={showMentalStateDialog}
        onClose={() => {
          setShowMentalStateDialog(false)
          // setMentalStateDialogData()
        }}
      />
      <Dialog
        PaperComponent={DescriptionSelectionDialogStyles}
        open={showDescriptionModal}
        onClose={() => setShowDescriptionModal(false)}
      >
        <ActivityDescriptionSelection
          mainActivityObject={activityObject}
          disabled={disabled}
          handleChangeMainActivityObject={handleChangeObject}
          closeModal={() => setShowDescriptionModal(false)}
          day={day}
        />
      </Dialog>
      <Dialog
        open={Boolean(toCreateItem)}
        onClose={closeCreationModal}
        PaperComponent={CreationDialogStyledComponent}
      >
        <ActivityFeatureCreationComponentInjector
          featureToCreate={toCreateItem}
          setFeatureToCreate={setToCreateItem}
          featureType={categoryToDailyPlayType(activityObject?.metaCategoryId, dropDownOptions)}
          onClose={closeCreationModal}
          changeActivityId={changeActivityId}
        />
      </Dialog>
    </div>
  )
}

function SectionComponent({ heading, first, children }) {
  return (
    <ActivitySectionContainer className="row g-2">
      {/* {!first && <hr></hr>} */}
      {heading && (
        <div className="col-md-12 pb-2">
          <div
            style={{
              fontSize: 'calc(1.275rem + 0.3vw)',
              fontWeight: 500,
              color: 'rgba(0, 0, 0, 0.87)',
            }}
          >
            {heading}
          </div>
        </div>
      )}
      {children}
    </ActivitySectionContainer>
  )
}

function ActivityComponentReadOnly({ imageObject, allCategories }) {
  const [showDetailsInText, setShowDetailsInText] = useState(false)
  const selectedCategory = allCategories.find((cat) => cat.id === imageObject?.metaCategoryId)

  return (
    <div className="row g-2">
      <SectionComponent heading="On Tap Details">
        <div className="py-2">
          {checkIfStringIsValid(imageObject.customDescription)
            ? imageObject.customDescription
            : imageObject.descriptionSentence}
        </div>
      </SectionComponent>
      <SectionComponent heading="Activity Details">
        <div style={{ zIndex: 0 }} className="col-md-12">
          <TrackUploadComponent trackObject={imageObject.track} entityName="Activity" disabled />
        </div>
        <div className="col-md-12 mt-2">
          <ButtonBase
            disableTouchRipple
            onClick={() => {
              setShowDetailsInText(true)
            }}
            sx={{ color: 'blue', textDecoration: 'underline' }}
          >
            Details in Text
          </ButtonBase>
        </div>
      </SectionComponent>
      <SectionComponent heading="Actual Aid Used">
        <div className="col-md-12">
          <CStrong>Category</CStrong>: {selectedCategory?.title}
        </div>
        <div className="col-md-12 mt-2">
          <CStrong>Aid</CStrong>: {imageObject?.activityId?.label || 'Loading...'}
        </div>
      </SectionComponent>
      <SectionComponent heading="More...">
        <div className="col-md-12">
          <CStrong>What it does</CStrong>: {imageObject.whatItDoes}
        </div>
        <div className="col-md-12 mt-2">
          <CStrong>Why offered</CStrong>: {imageObject.whyOffered}
        </div>
        <div className="col-md-12 mt-2">
          <CStrong>Internal Notes</CStrong>: {imageObject.internalNote}
        </div>
      </SectionComponent>
      <Dialog
        PaperComponent={DetailsInTextDialogStyles}
        open={showDetailsInText}
        onClose={() => setShowDetailsInText(false)}
      >
        <DialogTitle>Activity Details In Text</DialogTitle>
        <DialogContent>
          {!!imageObject?.content ? (
            <APArticleTextContainer>
              {getParseDataFromHtmlString(imageObject?.content || '')}
            </APArticleTextContainer>
          ) : (
            <div>No Content</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default React.memo(ActivityComponent)

const underLineStyled = {
  textDecoration: 'underline',
}

const restTypes = {
  my: 'my',
  openToAll: 'open',
}

// export function PlayActivity() {
//   const [prewallOpen, setPrewallOpen] = useState(false)
//   const [playerData, setPlayerData] = useState({
//     showPIP: false,
//     showLeavingEarly: false,
//     showPlayer: false,
//     selectedCard: '',
//     showCompletionFeedback: false,
//     source: '',
//     id: '',
//     otherData: {},
//   })

//   return (
//     <>
//       {playerData?.showPlayer && (
//         <CoursePlayerIsolated playerData={playerData} setPlayerData={setPlayerData} />
//       )}
//       <PrewallAndPaywall
//         id="journal_play"
//         secureData={setupData}
//         wallIsOpen={prewallOpen}
//         toggleWall={() => {
//           setPrewallOpen((prev) => !prev)
//         }}
//       />
//     </>
//   )
// }
